import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    IconButton,
    Menu,
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Badge,
    Button,
    CircularProgress,
} from '@mui/material'
import {
    Notifications as NotificationsIcon,
    NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material'
import { formatDistanceToNowStrict } from 'date-fns'
import { notificationsApi, AppNotification } from '@api/notifications'

// Notifications are polled, matching the interval the rest of the app already
// polls device status at. The backend has no push channel.
const POLL_INTERVAL_MS = 30000

const severityColor = (severity: AppNotification['severity']) => {
    switch (severity) {
        case 'critical':
        case 'high':
            return 'error.main'
        case 'medium':
            return 'warning.main'
        case 'low':
            return 'info.main'
        default:
            return 'text.secondary'
    }
}

const NotificationBell: React.FC = () => {
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [unreadCount, setUnreadCount] = useState(0)
    const [notifications, setNotifications] = useState<AppNotification[]>([])
    const [loading, setLoading] = useState(false)
    const open = Boolean(anchorEl)
    const mountedRef = useRef(true)

    const refreshUnreadCount = useCallback(async () => {
        try {
            const response = await notificationsApi.getUnreadCount()
            if (mountedRef.current) {
                setUnreadCount(response.data.data?.unread_count ?? 0)
            }
        } catch {
            // Silently ignore - the badge just won't update this cycle.
        }
    }, [])

    useEffect(() => {
        mountedRef.current = true
        refreshUnreadCount()
        const interval = setInterval(refreshUnreadCount, POLL_INTERVAL_MS)
        return () => {
            mountedRef.current = false
            clearInterval(interval)
        }
    }, [refreshUnreadCount])

    const loadNotifications = async () => {
        setLoading(true)
        try {
            const response = await notificationsApi.getNotifications({ page_size: 10 })
            setNotifications(response.data.data || [])
        } catch {
            setNotifications([])
        } finally {
            setLoading(false)
        }
    }

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        loadNotifications()
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleNotificationClick = async (notification: AppNotification) => {
        if (!notification.is_read) {
            try {
                await notificationsApi.markRead(notification.id)
                setNotifications((prev) =>
                    prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
                )
                refreshUnreadCount()
            } catch {
                // Navigate anyway even if marking as read failed.
            }
        }
        handleClose()
        if (notification.action_url) {
            navigate(notification.action_url)
        }
    }

    const handleMarkAllRead = async () => {
        try {
            await notificationsApi.markAllRead()
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
            setUnreadCount(0)
        } catch {
            // Ignore - user can retry.
        }
    }

    return (
        <>
            <IconButton
                onClick={handleOpen}
                size="small"
                sx={{ color: '#242424', mr: 0.5 }}
                aria-label="Notifications"
                aria-controls={open ? 'notifications-menu' : undefined}
                aria-haspopup="true"
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                id="notifications-menu"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        width: 360,
                        maxWidth: '90vw',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Button size="small" onClick={handleMarkAllRead}>
                            Mark all read
                        </Button>
                    )}
                </Box>
                <Divider />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            You're all caught up.
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ maxHeight: 420, overflowY: 'auto', py: 0 }}>
                        {notifications.map((notification) => (
                            <ListItemButton
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                sx={{
                                    alignItems: 'flex-start',
                                    borderLeft: '3px solid',
                                    borderLeftColor: notification.is_read ? 'transparent' : severityColor(notification.severity),
                                    backgroundColor: notification.is_read ? 'transparent' : 'rgba(248, 177, 51, 0.06)',
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" sx={{ fontWeight: notification.is_read ? 400 : 700 }}>
                                            {notification.title}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            {notification.message && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDistanceToNowStrict(new Date(notification.created_at), { addSuffix: true })}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Menu>
        </>
    )
}

export default NotificationBell
